import React, { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import PropTypes from 'prop-types';
import Tooltip from './Tooltip';
import { CloudArrowUpIcon } from '@heroicons/react/24/solid';

function FileInput({
    label,
    description,
    info,
    id,
    accept,
    limitSize,
    limitFiles,
    setFiles,
    error,
    isLogo,
    dimensions,
    setPreview,
    needed, 
    ...rest
}) {

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fileError, setFileError] = useState(error);

    const fileDrop = e => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        handleFiles(files);
    }

    const selectFile = e => {
        const files = e.target.files;
        handleFiles(files);
    }

    const verifyResolution = (file, callback) => {
        let img = new Image();
        img.src = window.URL.createObjectURL(file);
        img.onload = function () {
            let width = img.width,
                height = img.height;
            const size = { width: width, height: height }
            callback(size)
        };
    }
    //create url to get a preview image
    const showPreview = (file, w, h) => {
        let objectUrl = window.URL.createObjectURL(file[0]);
        const previewImage = {
            url: objectUrl,
            width: `${w}`,
            height: `${h}`,
            name: file[0].name
        }
        setPreview(previewImage);
    }

    //handle selected files
    const handleFiles = files => {
        setFileError(null);
        const fileArray = [...files]
        if (fileArray.length > limitFiles) {
            setFileError(`Número de archivos mayor a ${limitFiles}`);
        } else {
            let validFiles = []
            let error = null
            fileArray.forEach(file => {
                if (!isValidFile(file.name, accept)) {
                    error = fileArray.length == 1 ? 'Archivo incompatible' : 'Uno de tus archivos es incompatible';
                } else if (bytesToMegaBytes(file.size) > limitSize) {
                    error = fileArray.length == 1 ? `El tamaño máximo permitido es de ${limitSize} MB` : `Uno de tus archivos excede el tamaño máximo permitido: ${limitSize} MB`;
                } else if (isLogo) {
                    verifyResolution(file, (res) => {
                        if (res.width < dimensions[0] && res.height < dimensions[1]) {
                            setSelectedFiles([file]);
                            showPreview([file], res.width, res.height);
                        } else {
                            setFileError(`El archivo debe tener una dimensión máxima de ${dimensions[0]}x${dimensions[1]}`);
                        }
                    });
                } else {
                    validFiles.push(file);
                }
            });
            setSelectedFiles(validFiles);
            setFileError(error);
        }
    }

    //validate file extension
    const isValidFile = (fileName, extensions) => {
        if (extensions.length == 0) return true;
        const pattern = `.(${extensions.map(ext => ext.toLowerCase()).join('|')})$`;
        const regex = new RegExp(pattern, 'i');
        return regex.test(fileName);
    }

    const bytesToMegaBytes = bytes => (bytes / (1024 ** 2)).toFixed(3);

    //return files when selected files change
    useEffect(() => {
        if (selectedFiles.length > 0 && setFiles) setFiles(selectedFiles);
    }, [selectedFiles]);

    useEffect(() => {
        setFileError(error);
    }, [error]);

    return (
        <div className='w-full relative'>
            {label && (
                <label className='inline-block mb-1 text-gray-700 text-sm font-medium'>
                    {label} {needed && <span className='text-red-500'>*</span>}
                </label>
            )}
            {info && (
                <span className='inline-block ml-2 -mb-1'>
                    <Tooltip title={info} />
                </span>
            )}
            <div className={`transition-all flex justify-center border-2 border-dashed rounded-md bg-light-gray h-[7rem] lg:h-[7.5rem] ${fileError ? 'border-red-500' : 'border-gray-300'}`}
                onDrop={fileDrop}
                onDragOver={e => e.preventDefault()}
                onDragEnter={e => e.preventDefault()}
                onDragLeave={e => e.preventDefault()}>
                <input
                    type="file"
                    className="w-full h-full opacity-0 relative z-[5] cursor-pointer"
                    onChange={selectFile}
                    multiple={limitFiles > 1}
                    accept={accept?.join(', ')}
                    id={id}
                    {...rest} />
                <div className="space-y-1 text-center z-1 absolute p-1 mt-5 lg:mt-4 cursor-pointer">
                    <CloudArrowUpIcon className={`${fileError ? 'text-red-500' : 'text-gray-500'} transition-all mx-auto w-7 h-7`} />
                    <div className="text-sm text-gray-500">
                        <label htmlFor={id} className="underline relative cursor-pointer rounded-md font-medium text-blue-sky focus:border-blue-sky focus:outline-none focus:ring-1 focus:ring-blue-sky">
                            <span>{description}</span>
                        </label>
                        <span className="hidden lg:block pl-1">o arrastra y suelta</span>
                    </div>
                </div>
            </div>
            <Transition
                show={fileError != null}
                enter="transition-all ease-in"
                enterFrom="max-h-0 opacity-0"
                enterTo="max-h-[3rem] opacity-100"
                leave="transition-all ease-out"
                leaveFrom="max-h-[3rem] opacity-100"
                leaveTo="max-h-0 opacity-0">
                <span className='text-sm text-red-500 min-h-[1.25rem]'>{fileError}</span>
            </Transition>
        </div>
    )
}

FileInput.propTypes = {
    label: PropTypes.string,
    description: PropTypes.string,
    info: PropTypes.string,
    id: PropTypes.string,
    accept: PropTypes.arrayOf(PropTypes.string),
    limitSize: PropTypes.number,
    limitFiles: PropTypes.number,
    setFiles: PropTypes.func,
    error: PropTypes.string,
    isLogo: PropTypes.bool,
    dimensions: PropTypes.arrayOf(PropTypes.number),
    setPreview: PropTypes.func,
    needed: PropTypes.bool,
}

FileInput.defaultProps = {
    description: 'Sube un archivo',
    accept: [],
    //limit size in MB
    limitSize: 1,
    limitFiles: 1,
    isLogo: false,
    dimensions: [0, 0]
}

export default FileInput;